
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';

import { IVbSource } from '../IVbSource';
import { NativeCrossOrigin } from '../CrossOriginType';
import { PlaybackOption } from '../PlaybackOption';
import { SUBTITLE_OPTION_CAPTIONS } from '../VbSubtitleMenuButton.Component';
import { userAgentUtil } from '../UserAgentUtil';

import { VgStates } from './VgStates';
import { VideogularComponent } from './Videogular.Component';

import { C2paMp4Validator } from '../c2pa/C2paMp4Plugin';

export type VgNativePlayBlacklistFunc = (source: IVbSource, userAgent?: string) => boolean;

const CANPLAY_MAYBE: string = 'maybe';
const CANPLAY_PROBABLY: string = 'probably';

/**
 * Component to add a source of videos or audios. It will create a &lt;video&gt; or &lt;audio&gt; tag and usually will be above plugin tags.
 *
 * @param {array} vgNativePlayBlacklist An array of functions that accept a media source object and a user agent string. If the function returns true, native playback will be prevented.
 * @param {array} vgSrc Bindable array with a list of media sources. A media source is an object with two properties `src` and `type`. The `src` property must contains a trustful url resource.
 * @param {string} vgType String with "video" or "audio" values to set a <video> or <audio> tag inside <vg-media>.
 *
 * <pre>
 * {
 *		src: $sce.trustAsResourceUrl("path/to/video/videogular.mp4"),
 * 		type: "video/mp4"
 * }
 * </pre>
 *
 */
@Component({
	selector: 'vg-media',
	host: {
		'[class.disableCtrls]': 'isControlsDisabled',
	},
	template: `
		<video
			#mediaElement
			[ngClass]="[
				'video',
				isFixSubtitlePosition() ? 'subtitlesActive' : '',
				isRTLSubtitle() ? 'rtlSubtitles' : ''
			]"
			[crossOrigin]="crossOrigin"
			playsinline
			[attr.preload]="vgApi.autoLoad ? 'auto' : 'metadata'"
			[attr.poster]="dummyPoster"
			[attr.aria-label]="videoTitle || 'Video playback'"
			webkit-playsinline
			native-hls-stall-detector
			[activeSource]="vgApi.activeSource">
		</video>
	`,
	standalone: false,
	styleUrls: ['./VgMedia.Component.ng.less', './VbMediaClick.Directive.ng.less'],
})
export class VgMediaComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
	@Input() public crossOrigin: NativeCrossOrigin;
	@Input() public isControlsDisabled: boolean;
	@Input() public vgNativePlayBlacklist: VgNativePlayBlacklistFunc[];
	@Input() public vgSrc: PlaybackOption[];
	@Input() public videoTitle: string;
	@ViewChild('mediaElement', { static: true }) private mediaElementRef: ElementRef;

	private appliedSources: PlaybackOption[];
	public dummyPoster: string;
	public isRTLSubtitle = signal(false);
	public isFixSubtitlePosition = signal(false);
	private subscription: Subscription;
	private c2paMp4Validator: C2paMp4Validator;

	private readonly eventHandlers = {
		canplay: () => this.vgApi.onCanPlay(),
		canplaythrough: () => this.vgApi.onCanPlay(),
		contextmenu: event => event.preventDefault(),
		ended: () => this.vgApi.onComplete(),
		error: $event => this.vgApi.onVideoError($event),
		loadedmetadata: $event => this.vgApi.onLoadMetaData($event),
		pause: () => this.vgApi.onPause(),
		play: () => this.vgApi.onPlay(),
		playbackchange: () => this.vgApi.onPlaybackChange(),
		playing: () => this.vgApi.onStartPlaying(),
		progress: $event => this.vgApi.onProgress($event),
		seeked: $event => this.vgApi.onSeeked($event),
		seeking: $event => this.vgApi.onSeeking($event),
		timeupdate: $event => this.vgApi.onUpdateTime($event),
		volumechange: () => this.vgApi.onVolumeChange(),
		waiting: () => this.vgApi.onStartBuffering(),
		webkitendfullscreen: () => this.onWebkitEndFullScreen(),
	};

	private readonly textTracksHandlers = {
		addtrack: () => this.vgApi.onUpdateTrack(),
		removetrack: () => this.vgApi.onUpdateTrack()
	};

	constructor(
		public vgApi: VideogularComponent
	) {}

	public ngOnInit(): void {
		this.vgApi.mediaElement = this.mediaElementRef.nativeElement;
		this.setDummyPoster();

		this.toggleMediaElementListeners(true);
		this.toggleMediaTextTracksListeners(true);

		this.vgApi.onVideoReady();
	}

	public ngOnDestroy(): void {
		//if native playback video element not cleaned up then
		//it keeps fetching video segement.
		if (userAgentUtil.isMobile && userAgentUtil.isSafari && this.vgApi.mediaElement) {
			this.vgApi.mediaElement.pause();
			this.vgApi.mediaElement.removeAttribute('src');
			this.vgApi.mediaElement.load(); //needed to call load after clean up src to avoid browser crash.
		}
		this.subscription?.unsubscribe();
		this.c2paMp4Validator?.destroy();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.vgSrc) {
			this.onChangeSource(changes.vgSrc.currentValue, changes.vgSrc.previousValue);
		}
	}

	public ngAfterViewInit(): void {
		this.mediaElementRef.nativeElement.volume = this.vgApi.volume;
		this.subscription = this.vgApi.activeSubtitle$.subscribe(activeSubtitle => {
			this.isRTLSubtitle.set(typeof activeSubtitle.subtitle === 'object' ? activeSubtitle.subtitle?.isRTL : false);

			this.isFixSubtitlePosition.set(
				activeSubtitle.subtitle !== SUBTITLE_OPTION_CAPTIONS &&
				!userAgentUtil.isSafariIos
			);
		});
	}

	@HostBinding('class.vgMediaLiveAndoridChromeNotPlaying')
	public get isLiveAndroidChromeNotPlaying(): boolean {
		return userAgentUtil?.isAndroidChrome
			&& this.vgApi?.activeSource?.isLive
			&& this.vgApi?.currentState !== VgStates.PLAY;
	}

	private applySourceToMediaElement(source: PlaybackOption): void {
		const video = this.vgApi.mediaElement;
		video.setAttribute('type', source.type);

		if(source.srcObject) {
			if('srcObject' in (video as any)) {
				try {
					video.srcObject = source.srcObject;
				} catch (err) {
					if (err.name !== 'TypeError') {
						throw err;
					}
					video.setAttribute('src', URL.createObjectURL(source.srcObject));
				}
			} else {
				video.setAttribute('src', URL.createObjectURL(source.srcObject));
			}
		}
		else {
			video.setAttribute('src', source.src);
		}

		this.vgApi.changeActiveSource(source);
	}

	private isNativePlayBlacklisted(source: IVbSource): boolean {
		const userAgent = window.navigator.userAgent;

		if (!source || !Array.isArray(this.vgNativePlayBlacklist)) {
			return false;
		}

		return this.vgNativePlayBlacklist.reduce((accumulator: boolean, currentNativeBlacklistFunc: VgNativePlayBlacklistFunc) =>
			accumulator ||
			(
				currentNativeBlacklistFunc instanceof Function &&
				currentNativeBlacklistFunc(source, userAgent)
			)
		, false);
	}

	private onAppliedSourcesChanged(): void {
		let canPlay: string;
		let isSourceApplied: boolean = false;
		const normalizedSources: PlaybackOption[] = Array.isArray(this.appliedSources) ? this.appliedSources : [this.appliedSources];

		if (!normalizedSources.length) {
			this.vgApi.changeActiveSource(null);
			return;
		}

		//cleanup C2PA validator instance if any
		this.c2paMp4Validator?.destroy();
		this.c2paMp4Validator = null;

		// try each given source in sequence until one can be applied
		for (const currentSource of normalizedSources) {
			if (!currentSource) {
				continue;
			}
			this.vgApi.currentSourceC2paEnabled = currentSource.isC2paEligible;

			if (!this.isNativePlayBlacklisted(currentSource)) {
				//check to see if the media element can play the current source
				canPlay = this.vgApi.rawMediaElement.canPlayType(currentSource.type);

				//if it's usable, apply the current source
				if (canPlay === CANPLAY_MAYBE || canPlay === CANPLAY_PROBABLY) {
					isSourceApplied = true;

					this.applySourceToMediaElement(currentSource);
					if (this.vgApi.c2paConfig && this.vgApi.currentSourceC2paEnabled) {
						this.c2paMp4Validator = new C2paMp4Validator(this.vgApi.c2paConfig);
						this.c2paMp4Validator.init(currentSource.src);
					}
					break;
				}
			}

			if (this.vgApi.numPlaybackPlugins) {
				//see if there's a plugin configured to handle this type (it will handle the applying the new src)
				const sourceWithUnwrapped: IVbSource = { ...currentSource };
				if ((sourceWithUnwrapped.src as any).$$unwrapTrustedValue) {
					sourceWithUnwrapped.src = (sourceWithUnwrapped.src as any).$$unwrapTrustedValue();
				}
				const playbackPluginResult = this.vgApi.attemptPlaybackThroughPlugin(sourceWithUnwrapped);

				if (playbackPluginResult) {
					isSourceApplied = true;
					this.vgApi.playbackPluginUnload = playbackPluginResult;
					this.vgApi.changeActiveSource(currentSource);
					break;
				}
			}

		}

		if (isSourceApplied) {
			// Android 2.3 support: https://github.com/2fdevs/videogular/issues/187
			if (userAgentUtil.isMobile) {
				this.vgApi.rawMediaElement.load();
			}

			//autoplay behavior
			window.setTimeout(() => {
				if (this.vgApi.autoPlay && !userAgentUtil.isMobile) {
					this.vgApi.play();
				}
			});
		} else { //no source applied
			this.vgApi.onVideoError();
		}
	}

	private onChangeSource(newValue: PlaybackOption[], oldValue: PlaybackOption[]): void {
		if (newValue && (!this.appliedSources || JSON.stringify(newValue) !== JSON.stringify(oldValue)) && this.appliedSources !== newValue) {
			this.appliedSources = newValue;

			if (this.vgApi.currentState !== VgStates.PLAY) {
				this.vgApi.currentState = VgStates.STOP;
			}

			if (this.vgApi.playbackPluginUnload) {
				this.vgApi.playbackPluginUnload();
				this.vgApi.playbackPluginUnload = null;

				this.toggleMediaElementListeners(false);
				this.toggleMediaElementListeners(true);

				this.toggleMediaTextTracksListeners(false);
				this.toggleMediaTextTracksListeners(true);

				// safety check to work around volume not getting applied if video element is substituted
				const mediaElement = this.vgApi.rawMediaElement;
				if (mediaElement.volume !== this.vgApi.volume) {
					mediaElement.volume = this.vgApi.volume;
					mediaElement.muted = !this.vgApi.volume;
				}
			}

			this.vgApi.sources = this.appliedSources;
			this.onAppliedSourcesChanged();
		}
	}

	private onWebkitEndFullScreen(): void {
		if (userAgentUtil.isSafariIos) {
			this.vgApi.oniOSEndFullScreen();
		}
	}

	private toggleMediaElementListeners(isAdd: boolean): void {
		this.toggleEventListners(isAdd, this.vgApi.mediaElement, this.eventHandlers);
	}

	private toggleMediaTextTracksListeners(isAdd: boolean): void {
		this.toggleEventListners(isAdd, this.vgApi.mediaElement.textTracks, this.textTracksHandlers);
	}

	private toggleEventListners(isAdd: boolean, target: EventTarget, eventHandlers: any): void {
		const method: string = isAdd ?
			'addEventListener' :
			'removeEventListener';

		Object.entries(eventHandlers)
			.forEach(([event, handler]) => target[method](event, handler));
	}

	private setDummyPoster(): void {
		//A Data URI for a transparent pixel PNG. Used for dummy poster.
		//it is needed in android webview embed scenario else android webview defaulting to android poster and video playback was failing.
		this.dummyPoster = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';
	}
}
